/**
 * The release an item is drawn under on the roadmap, and the statuses that close a target. A leaf,
 * re-exported by `roadmap-rules.ts`: the launch entry places an item by this rule (the list's server
 * filter) and needs none of the bands' rules (T-0770, the entry budget).
 */

/**
 * The release statuses at which a target stops placing an item (AC-14).
 *
 * A released release is history and a cancelled one will never ship: an item still only aimed at
 * either missed its target, and drawing it under that release would claim it shipped there or will.
 */
export const ROADMAP_TARGET_CLOSED_RELEASE_STATUSES = ['released', 'cancelled'] as const;

export interface IRoadmapReleaseInput {
  fixedInReleaseId?: string | null;
  targetReleaseId?: string | null;
  /** The target release's status; unknown (null) is treated as open. */
  targetReleaseStatus?: string | null;
}

/**
 * The release an item is drawn under on the roadmap (AC-14, RR-7).
 *
 * The release it landed in, when it has one: that is a record of where the work went. Otherwise the
 * release it is aimed at, while that release is still to ship — a target is the link that answers
 * "what is planned" (ADR-036), and a release that showed 0 items while items aimed at it was I-0418.
 * The engine renders this as the derived field `roadmapReleaseId`.
 */
export function roadmapReleaseIdFor(input: IRoadmapReleaseInput): string | null {
  if (input.fixedInReleaseId) return input.fixedInReleaseId;
  if (!input.targetReleaseId) return null;
  const closed = (ROADMAP_TARGET_CLOSED_RELEASE_STATUSES as readonly string[]).includes(
    input.targetReleaseStatus ?? '',
  );
  return closed ? null : input.targetReleaseId;
}
